/**
 * InferenceProgress.js
 *
 * Displays real-time progress during landmark extraction.
 * Receives the latest progress object from the polling loop in api.js
 * and renders a stage-appropriate UI.
 *
 * Usage in your App.js / parent component:
 *
 *   import InferenceProgress from './InferenceProgress';
 *
 *   const [progress, setProgress] = useState(null);
 *   const [isRunning, setIsRunning] = useState(false);
 *   const [result, setResult] = useState(null);
 *
 *   const handleUpload = async (file) => {
 *     setIsRunning(true);
 *     setResult(null);
 *     try {
 *       const data = await runInference(file, setProgress);
 *       setResult(data);
 *     } catch (err) {
 *       console.error(err);
 *     } finally {
 *       setIsRunning(false);
 *       setProgress(null);
 *     }
 *   };
 *
 *   // In JSX:
 *   {isRunning && <InferenceProgress progress={progress} />}
 */

import React from "react";

// ── Stage labels shown to the user ────────────────────────────────────────────
const STAGE_LABELS = {
  submitting:     "Uploading image...",
  in_queue:       "Waiting for GPU worker...",
  loading_models: "Loading AI models...",
  yolo_detection: "Detecting landmark regions (YOLO)...",
  segmenting:     null,   // built dynamically below
};

/**
 * Formats a landmark key like "LIST_A01_l" into something more readable:
 * "LIST A01 (left)"
 */
function formatLandmarkName(raw) {
  if (!raw) return "";
  return raw
    .replace(/_l$/, " (left)")
    .replace(/_r$/, " (right)")
    .replace(/_/g, " ");
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function InferenceProgress({ progress }) {
  // No progress yet — show generic spinner
  if (!progress) {
    return (
      <div style={styles.container}>
        <Spinner />
        <p style={styles.stageLabel}>Connecting to inference server...</p>
      </div>
    );
  }

  const { stage, current, total, landmark } = progress;

  const isSegmenting = stage === "segmenting";
  const percent       = isSegmenting && total > 0
    ? Math.round((current / total) * 100)
    : null;

  const stageText = isSegmenting
    ? `Segmenting landmark ${current} of ${total}`
    : STAGE_LABELS[stage] ?? "Processing...";

  return (
    <div style={styles.container}>
      <Spinner />

      {/* Current stage */}
      <p style={styles.stageLabel}>{stageText}</p>

      {/* Landmark name — only shown during segmentation */}
      {isSegmenting && landmark && (
        <p style={styles.landmarkName}>
          <span style={styles.landmarkIcon}>📍</span>
          {formatLandmarkName(landmark)}
        </p>
      )}

      {/* Progress bar — only shown during segmentation */}
      {isSegmenting && percent !== null && (
        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressBar,
              width: `${percent}%`,
            }}
          />
          <span style={styles.progressLabel}>{percent}%</span>
        </div>
      )}

      {/* Landmark count */}
      {isSegmenting && (
        <p style={styles.subLabel}>
          {current} / {total} landmarks extracted
        </p>
      )}
    </div>
  );
}

// ── Spinner sub-component ─────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={styles.spinnerWrapper}>
      <div style={styles.spinner} />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Inline styles keep this component dependency-free.
// Override by passing a className prop and using CSS modules/Tailwind instead.

const styles = {
  container: {
    display:        "flex",
    flexDirection:  "column",
    alignItems:     "center",
    gap:            "12px",
    padding:        "32px 24px",
    background:     "#f8faff",
    border:         "1px solid #dce6f7",
    borderRadius:   "12px",
    textAlign:      "center",
    fontFamily:     "system-ui, sans-serif",
    maxWidth:       "420px",
    margin:         "0 auto",
  },
  spinnerWrapper: {
    width:  "48px",
    height: "48px",
  },
  spinner: {
    width:           "48px",
    height:          "48px",
    borderRadius:    "50%",
    border:          "4px solid #e2e8f4",
    borderTopColor:  "#3b5fe2",
    animation:       "spin 0.8s linear infinite",
    // CSS animation defined globally — see InferenceProgress.css or add to your global stylesheet:
    // @keyframes spin { to { transform: rotate(360deg); } }
  },
  stageLabel: {
    margin:     0,
    fontSize:   "15px",
    fontWeight: "600",
    color:      "#2c3e7a",
  },
  landmarkName: {
    margin:       0,
    fontSize:     "14px",
    color:        "#4a5568",
    background:   "#eef2ff",
    padding:      "6px 14px",
    borderRadius: "20px",
    display:      "flex",
    alignItems:   "center",
    gap:          "6px",
  },
  landmarkIcon: {
    fontSize: "14px",
  },
  progressTrack: {
    position:     "relative",
    width:        "100%",
    height:       "10px",
    background:   "#e2e8f4",
    borderRadius: "5px",
    overflow:     "hidden",
  },
  progressBar: {
    position:       "absolute",
    top:            0,
    left:           0,
    height:         "100%",
    background:     "linear-gradient(90deg, #3b5fe2, #6b8ef0)",
    borderRadius:   "5px",
    transition:     "width 0.4s ease",
  },
  progressLabel: {
    // visually hidden — screen reader accessible
    position: "absolute",
    width:    "1px",
    height:   "1px",
    overflow: "hidden",
    clip:     "rect(0,0,0,0)",
  },
  subLabel: {
    margin:   0,
    fontSize: "13px",
    color:    "#718096",
  },
};
